import { ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import composeRefs from '@seznam/compose-react-refs';

import connectFieldsStyles from '@components/primitives/ConnectedFields/styles.module.css';
import { useFormField } from '@components/primitives/FormField';
import { useAutoExpand } from '@components/primitives/Textarea/useAutoExpand';
import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

export type TextareaProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  'size'
> & {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  error?: boolean;
  width?: 'auto';
  autoExpand?: boolean;
  preventNewLines?: boolean;
  variant?: 'default' | 'plain';
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      className,
      size = 'md',
      error,
      onBlur,
      onFocus,
      width,
      onChange,
      onPaste,
      autoExpand = true,
      preventNewLines = false,
      variant = 'default',
      ...props
    },
    externalRef
  ) {
    const formField = useFormField();
    const [focused, setFocused] = useState(false);

    const autoExpandRef = useAutoExpand({ enabled: autoExpand });

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      e.preventDefault();
      let text = e.clipboardData.getData('text/plain');
      if (preventNewLines) {
        text = text.replace(/\n\s*/g, ' ');
      }
      document.execCommand('insertText', false, text);
      onPaste?.(e);
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const container = e.target as HTMLDivElement;
      container.querySelector('input')?.focus();
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.currentTarget.closest('form')?.requestSubmit();
      } else if (preventNewLines && e.key === 'Enter') {
        e.preventDefault();
      }
    };

    return (
      <div
        className={cn(
          connectFieldsStyles.connectableField,
          css.textarea,
          className
        )}
        data-focused={focused}
        data-size={size}
        data-error={error ?? Boolean(formField?.error)}
        data-width={width}
        onClick={handleClick}
        data-disabled={props.disabled}
        data-variant={variant}
      >
        <textarea
          id={formField?.id ?? props.id}
          ref={composeRefs(autoExpandRef, externalRef)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={onChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          {...props}
        />
      </div>
    );
  }
);
